import { ArrayUtil, FileUtil, Model, ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { ImageName } from "@mcleod/images";
import {
    ChangeEvent, DataSource, DataSourceMode, DynamicDbDisplayValueSearchEvent, Layout,
    LayoutProvidedTaskDecoratorProps, LookupModelSelectionEvent, ReportPreviewTabSource, SlideoutDecorator, Tab,
    TaskDecorator, Textbox, Toast, ValidationResult
} from "@mcleod/components";
import { AutogenLayoutImportFile } from "./autogen/AutogenLayoutImportFile";
import { ImportFileResultTable, ImportFileResultTableType } from "./ImportFileResultTable";
import { ImportFileExceptionTable } from "./ImportFileExceptionTable";

const DATA_TYPE_JOURNAL = "J";

export class ImportFile extends AutogenLayoutImportFile {
    private beginningBalanceWarningShown = false;

    override onLoad() {
        this.textboxId.addlValidationCallback = this.validateSelectedProfile;
        this.textboxStartRow.addlValidationCallback = () => this.validateStartEndRows(this.textboxStartRow);
        this.textboxEndRow.addlValidationCallback = () => this.validateStartEndRows(this.textboxEndRow);
        this.attachmentFile.validationCallback = (file: File): string => { return this.validateFileType(file) };
        this.sourceImportFile.afterGetDataboundValues = this.afterGetDataboundValues;
    }

    public override get taskDecoratorProps(): Partial<LayoutProvidedTaskDecoratorProps> {
        return {
            executeButtonCaption: "Import Data",
            confirmationDialogMessage: "Are you sure you want to import?",
            confirmationDialogTitle: "Confirm Import",
            processLogDescription: "Import Data Files",
            adjustReportPreviewDataAfterSearch: (tabSource: ReportPreviewTabSource[], responseData: ModelRow[]) => {
                this.addEditResultTabs(tabSource, responseData);
            },
            doAfterSearch: (_tabSource: ReportPreviewTabSource[], responseData: ModelRow[]) => {
                this.displayEditSummaryMessage(responseData);
            },
            adjustReportPreviewDataAfterPost: (tabSource: ReportPreviewTabSource[], responseData: any) => {
                this.addPostResultTabs(tabSource, responseData);
            },
            doAfterPost: (_tabSource: ReportPreviewTabSource[], response: any) => {
                this.displayPostSummaryMessage(response);
            },
            doOnResetForm: () => {
                this.syncActionTypeEnabled();
            }
        };
    }

    private addEditResultTabs(tabSource: ReportPreviewTabSource[], responseData: ModelRow[]) {
        const data = ArrayUtil.getFirstElement(responseData);
        if (data != null) {
            const editListResults = data.get("edit_row_results", []);
            const editListTab = this.createImportFileResultTab(editListResults, ImportFileResultTableType.EDIT);
            if (editListTab != null) {
                tabSource.push(editListTab);
            }

            const exceptions = data.get("exceptions", []);
            const exceptionTab = this.createImportFileExceptionTab(exceptions);
            if (exceptionTab != null) {
                tabSource.push(exceptionTab);
            }
        }
    }

    private displayEditSummaryMessage(responseData: ModelRow[]) {
        const data = ArrayUtil.getFirstElement(responseData);
        if (data != null) {
            const toastMessage = data.getString("summary_message", "");
            if (!StringUtil.isEmptyString(toastMessage)) {
                Toast.showSuccessToast("Edit List Generated", toastMessage);
            }
        }
    }

    private addPostResultTabs(tabSource: ReportPreviewTabSource[], response: any) {
        const responseData = ArrayUtil.getFirstElement(response?.data);
        if (responseData != null) {
            const editListResults = responseData.edit_row_results ?? [];
            const editListTab = this.createImportFileResultTab(editListResults, ImportFileResultTableType.EDIT);
            if (editListTab != null) {
                tabSource.push(editListTab);
            }

            const postResults = responseData.post_row_results ?? [];
            const postTab = this.createImportFileResultTab(postResults, ImportFileResultTableType.POST);
            if (postTab != null) {
                tabSource.push(postTab);
            }

            const exceptions = responseData.exceptions ?? [];
            const exceptionTab = this.createImportFileExceptionTab(exceptions);
            if (exceptionTab != null) {
                tabSource.push(exceptionTab);
            }
        }
    }

    private displayPostSummaryMessage(response: any) {
        const responseData = ArrayUtil.getFirstElement(response?.data);
        if (responseData != null) {
            const toastMessage = responseData.summary_message;
            if (!StringUtil.isEmptyString(toastMessage)) {
                Toast.showSuccessToast("Import Complete", toastMessage);
            }
        }
    }

    private createImportFileResultTab(results: string[][], type: ImportFileResultTableType): Tab {
        if (ArrayUtil.isEmptyArray(results)) {
            return null;
        }
        const importFileResultTable = new ImportFileResultTable(type, results);
        const tab = new Tab({
            components: [ importFileResultTable ],
            caption: importFileResultTable.tabCaption
        });
        return tab;
    }

    private createImportFileExceptionTab(exceptions: any[]): Tab {
        if (ArrayUtil.isEmptyArray(exceptions)) {
            return null;
        }
        const importFileExceptionTable =
            Layout.getLayout("lme/general/dataimport/ImportFileExceptionTable") as ImportFileExceptionTable;
        importFileExceptionTable.addLayoutLoadListener(() => {
            importFileExceptionTable.data = exceptions;
        });
        const tab = new Tab({
            components: [ importFileExceptionTable ],
            caption: importFileExceptionTable.tabCaption
        });
        tab.imageName = ImageName.circleError
        tab.imageColor = "error";
        return tab;
    }

    async loadForProfile(profileId: string) {
        // Search for the specified profile in the same way that would occur if the profile ID was being selected by the
        // user in the type-ahead field.
        const fieldList = {
            layoutName: "lme/general/dataimport/FileImportProfile",
            extraFields: "lme/general/dataimport/fileimport-profile-lookup"
        };
        const modelSearchResult = await Model.search("lme/general/dataimport/fileimport-profile", { id: profileId },
            fieldList);
        const selectedProfile = ArrayUtil.getFirstElement(modelSearchResult.modelRows);
        if (selectedProfile) {
            const idLookupModelData = new ModelRow("lme/general/dataimport/fileimport-profile", false, {
                id: profileId,
                descr: selectedProfile.getString("descr", null),
                data_type: selectedProfile.getString("data_type", null),
                is_active: selectedProfile.getString("is_active", null)
            });
            this.sourceImportFile.searchRow.setValueAndLookupModelData("id", profileId, idLookupModelData);
        }
        await this.setFieldsFromProfile(selectedProfile);
    }

    private validateSelectedProfile = (): ValidationResult => {
        let validationMessage: string;
        const selectedProfile = this.textboxId.getFirstLookupModelData();
        if (selectedProfile?.getBoolean("is_active", false) !== true) {
            validationMessage = "The selected file import profile is not active."
        } else if (selectedProfile?.getString("data_type", null) === "BS") {
            validationMessage =
                "Bank Statement Transactions can only be imported from the Bank Reconciliation Processing screen.";
        }
        if (!StringUtil.isEmptyString(validationMessage)) {
            return {
                component: this.textboxId,
                isValid: false,
                validationMessage
            };
        }
        return null;
    }

    private validateStartEndRows = (textbox: Textbox): ValidationResult => {
        const startRowValue = this.getEffectiveStartEndRowValue(this.textboxStartRow);
        const endRowValue = this.getEffectiveStartEndRowValue(this.textboxEndRow);
        if (startRowValue > endRowValue) {
            return {
                component: textbox,
                isValid: false,
                validationMessage: "The start row must be less than or equal to the end row.  Leave the end row" +
                    " value blank to process rows through the end of the worksheet."
            };
        }
        this.textboxStartRow.resetValidation();
        this.textboxEndRow.resetValidation();
    }

    private getEffectiveStartEndRowValue(textbox: Textbox): number {
        if (!StringUtil.isEmptyString(textbox.text)) {
            return Number(textbox.text);
        }
        return textbox === this.textboxStartRow ? 1 : Number.MAX_SAFE_INTEGER;
    }

    private validateFileType(file: File): string {
        const fileExtension = FileUtil.getExtension(file);
        return fileExtension.toLocaleLowerCase() !== ".xls" ? "Data may only be imported from .xls files" : null;
    }

    private afterGetDataboundValues = (row: ModelRow, _dataSource: DataSource) => {
        this.attachmentFile.updateBoundData(row, DataSourceMode.ADD);
    }

    textboxIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const selectedProfile = event.selection;
        this.setFieldsFromProfile(selectedProfile);
    }

    textboxActionTypeBeforeDynamicDbDisplayValueSearch(event: DynamicDbDisplayValueSearchEvent) {
        this.addDataTypeToEvent(event);
    }

    private addDataTypeToEvent(event: DynamicDbDisplayValueSearchEvent) {
        const selectedProfile = this.textboxId.getFirstLookupModelData();
        const dataType = selectedProfile?.getString("data_type", null);
        if (!StringUtil.isEmptyString(dataType)) {
            event.addAddlValue("data_type", dataType);
        }
    }

    private async setFieldsFromProfile(selectedProfile: ModelRow) {
        const searchRow = this.sourceImportFile.searchRow;
        await this.updateActionTypeForProfile(searchRow, selectedProfile);
        this.updateWorksheetForProfile(searchRow, selectedProfile);
        this.updateStartRowForProfile(searchRow, selectedProfile);
        this.updateMatchOnPrevCodeForProfile(searchRow, selectedProfile);
        this.updateDeleteExistingTimecardsForProfile(selectedProfile);
        this.updateJournalFieldsForProfile(searchRow, selectedProfile);

        // This is unfortunate...we don't have a field update listener in DataSource that reacts to changes to the
        // search row like we do for normal data rows.
        this.sourceImportFile.boundSearchComponents.forEach(component => {
            component.displayData(this.sourceImportFile.searchRow, [ this.sourceImportFile.searchRow ], 0);
        });
    }

    private async updateActionTypeForProfile(searchRow: ModelRow, selectedProfile: ModelRow) {
        await this.resetActionTypeDropdownItems();
        this.syncActionTypeEnabled(searchRow);
        const selectedActionType = selectedProfile?.getString("action_type", null) ?? null;
        // Setting the actionType two ways (on the row and via the Textbox) gets around a timing issue with
        // dynamic DB/Display values
        this.textboxActionType.setSelectedItemFromValue(selectedActionType);
        searchRow.set("action_type", selectedActionType);
    }

    private syncActionTypeEnabled(searchRow: ModelRow = this.sourceImportFile.searchRow) {
        this.textboxActionType.enabled = !StringUtil.isEmptyString(searchRow.getString("id", null));
    }

    private async resetActionTypeDropdownItems() {
        this.sourceImportFile.searchRow.set("action_type", null);
        await this.textboxActionType.refreshDynamicItems();
    }

    private updateWorksheetForProfile(searchRow: ModelRow, selectedProfile: ModelRow) {
        searchRow.set("worksheet", selectedProfile?.getNumber("worksheet", null) ?? null);
    }

    private updateStartRowForProfile(searchRow: ModelRow, selectedProfile: ModelRow) {
        searchRow.set("start_row", selectedProfile?.getNumber("start_row", null) ?? null);
    }

    private updateMatchOnPrevCodeForProfile(searchRow: ModelRow, selectedProfile: ModelRow) {
        const matchOnPrevCodeAllowed = selectedProfile?.getBoolean("match_on_prev_code_allowed", false) ?? false;
        this.switchMatchOnPrevCode.visible = matchOnPrevCodeAllowed;
        if (matchOnPrevCodeAllowed) {
            searchRow.set("match_on_prev_code", selectedProfile.getString("match_on_prev_code", "N"));
        } else {
            searchRow.set("match_on_prev_code", "N");
        }
    }

    private updateDeleteExistingTimecardsForProfile(selectedProfile: ModelRow) {
        const deleteExistingTimecardsAllowed = selectedProfile?.getBoolean("delete_existing_timecards_allowed", false)
            ?? false;
        this.switchDeleteExistingTimecards.visible = deleteExistingTimecardsAllowed;
        this.switchDeleteExistingTimecards.checked = false;
    }

    private updateJournalFieldsForProfile(searchRow: ModelRow, selectedProfile: ModelRow) {
        const isJournal = selectedProfile?.getString("data_type", null) === DATA_TYPE_JOURNAL;
        this.textboxTransactionDate.visible = isJournal;
        this.switchBeginningBalance.visible = isJournal;
        if (!isJournal) {
            searchRow.set("transaction_date", null);
            searchRow.set("beginning_balance", "N");
            this.switchBeginningBalance.checked = false;
            this.beginningBalanceWarningShown = false;
        }
    }

    switchBeginningBalanceOnChange(event: ChangeEvent) {
        if (!this.beginningBalanceWarningShown && event.newValue === true) {
            this.beginningBalanceWarningShown = true;
            CommonDialogs.showDialog(
                "This setting should only be used as advised and only on the initial setup of the general " +
                "ledger. Net change entries should not be marked as beginning balance during the initialization " +
                "of the general ledger. Failure to use this setting properly can cause general ledger errors " +
                "that require support assistance to correct.",
                { title: "Warning" }
            );
        }
    }

    buttonBrowseFilesOnClick() {
        FileUtil.showOpenFileDialog().then(file => {
            if (file != null) {
                this.attachmentFile.removeAttachment();
                this.textboxFileName.clear();
                this.attachmentFile.setAttachmentWithUI(file);
                this.textboxFileName.text = this.attachmentFile.fileName;
            }
        });
    }

    public static openInSlideout(profileId: string, profileDescr: string) {
        const layoutWrapper = new Layout({ // Decorators want to hold layouts, not plain panels
            needsServerLayout: false,
            paddingTop: 12,
            paddingRight: 12,
            paddingBottom: 12
        });
        const layoutImportFile = Layout.getLayout("lme/general/dataimport/ImportFile") as ImportFile;
        const taskDecorator = new TaskDecorator({ layout: layoutImportFile });
        layoutImportFile.addLayoutLoadListener(async () => {
            if (!StringUtil.isEmptyString(profileId)) {
                await layoutImportFile.loadForProfile(profileId);
                taskDecorator.resetDefaults();
            }
        });
        layoutWrapper.add(taskDecorator);
        new SlideoutDecorator({
            layout: layoutWrapper,
            width: "95%",
            fillVerticalSpace: true,
            title: this.buildSlideoutTitle(profileDescr)
        });
    }

    private static buildSlideoutTitle(profileDescr: string): string {
        if (!StringUtil.isEmptyString(profileDescr)) {
            return `Import File - ${profileDescr}`
        }
        return "Import File";
    }
}
